<template>
  <AppDialog
    v-model="dialog"
    :title="title"
    max-width="800px"
    @close="close"
  >
    <v-card-text>
      <v-form
        ref="form"
        v-model="valid"
      >
        <v-container>
          <v-row>
            <v-col cols="3">
              <v-text-field
                v-model="user.firstName"
                name="first_name"
                label="First Name"
                required
              />
            </v-col>
            <v-col cols="3">
              <v-text-field
                v-model="user.lastName"
                name="last_name"
                label="Last Name"
                required
              />
            </v-col>
            <v-col cols="3">
              <v-text-field
                v-model="user.email"
                name="email"
                label="Email"
                required
              />
            </v-col>
            <v-col cols="3">
              <v-select
                :model-value="selectedRoleId"
                :items="roles"
                item-title="name"
                item-value="id"
                variant="outlined"
                label="Role"
                name="role"
                @update:model-value="onRoleChange"
              >
                <scale-loader :loading="roleLoading" />
              </v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="selectedScopes"
                :items="scopeOptions"
                item-title="title"
                item-value="value"
                variant="outlined"
                label="Permissions"
                name="scopes"
                multiple
                chips
                closable-chips
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="9">
              <v-text-field
                v-model="password"
                name="password"
                type="password"
                label="Password"
                :required="changePassword"
                :rules="passwordRules"
                :disabled="!changePassword"
              />
            </v-col>
            <v-col>
              <v-checkbox
                v-if="user && user.id"
                v-model="changePassword"
                name="change_password"
                label="Change Password?"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <b class="red-text"> {{ message }}</b>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-btn @click="close">
        <v-icon>mdi-close</v-icon>
        Cancel
      </v-btn>
      <v-spacer />
      <v-btn
        color="primary"
        :disabled="saving"
        @click="save"
      >
        <v-icon>mdi-check</v-icon>
        <span v-show="!saving">{{ button }}</span>
        <pulse-loader :loading="saving" />
      </v-btn>
    </v-card-actions>
  </AppDialog>
  <v-dialog
    v-model="roleChangeConfirm"
    persistent
    max-width="420px"
  >
    <v-card>
      <v-card-title class="text-h6">
        Change Role?
      </v-card-title>
      <v-card-text>
        Changing the role will reset permissions to the new role's preset.
        Any manual permission changes will be lost.
      </v-card-text>
      <v-card-actions>
        <v-btn @click="cancelRoleChange">
          Cancel
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          @click="confirmRoleChange"
        >
          Continue
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import AppDialog from "@/components/AppDialog";
import PulseLoader from "vue-spinner/src/PulseLoader";
import ScaleLoader from "vue-spinner/src/ScaleLoader";

const SCOPE_OPTIONS = [
  { title: "Sightings - Read", value: "sightings:read" },
  { title: "Sightings - Write", value: "sightings:write" },
  { title: "Whales - Read", value: "whales:read" },
  { title: "Whales - Write", value: "whales:write" },
  { title: "Images - Read", value: "images:read" },
  { title: "Images - Write", value: "images:write" },
  { title: "Users - Read", value: "user:read" },
  { title: "Users - Write", value: "user:write" },
  { title: "Reports - Read", value: "reports:read" },
  { title: "Reports - Write", value: "reports:write" },
];

export default {
  components: {
    AppDialog,
    PulseLoader,
    ScaleLoader,
  },

  data() {
    return {
      selectedRoleId: null,
      changePassword: null,
      password: null,
      valid: true,
      scopeOptions: SCOPE_OPTIONS,
      selectedScopes: [],
      previousRoleId: null,
      roleChangeConfirm: false,
      pendingRoleId: null,
      passwordRules: [(v) => (!!v && v.length >= 6) || "Password must be at least 6 characters!"],
    };
  },
  computed: {
    ...mapGetters({
      saving: "user/details/saving",
      saved: "user/details/saved",
      user: "user/details/user",
      dialog: "user/details/dialog",
      button: "user/details/button",
      title: "user/details/title",
      roles: "users/roles",
      message: "user/details/message",
      roleLoading: "users/loading"
    }),
  },
  watch: {
    user(user) {
      this.selectedRoleId = user ? user.roleId : null;
      this.previousRoleId = user ? user.roleId : null;
      this.selectedScopes = user && user.scopes ? user.scopes.split(" ").filter((s) => s !== "me") : [];
      this.setMessage("");
      this.password = user ? user.password : null;
      this.changePassword = !(this.user && this.user.id);
    },
  },
  methods: {
    ...mapActions({
      update: "user/details/update",
      create: "user/details/create",
      close: "user/details/close",
      setMessage: "user/details/setMessage",
    }),
    presetForRole(roleId) {
      const role = this.roles.find((r) => r.id === roleId);
      return role && role.scopes ? role.scopes.filter((s) => s !== "me") : [];
    },
    scopesMatchPreset(scopes, roleId) {
      const preset = this.presetForRole(roleId);
      if (scopes.length !== preset.length) {
        return false;
      }
      return scopes.every((s) => preset.includes(s));
    },
    onRoleChange(newRoleId) {
      if (
        this.previousRoleId &&
        !this.scopesMatchPreset(this.selectedScopes, this.previousRoleId)
      ) {
        this.pendingRoleId = newRoleId;
        this.roleChangeConfirm = true;
        return;
      }
      this.applyRole(newRoleId);
    },
    applyRole(roleId) {
      this.selectedRoleId = roleId;
      this.previousRoleId = roleId;
      this.selectedScopes = this.presetForRole(roleId);
    },
    confirmRoleChange() {
      this.applyRole(this.pendingRoleId);
      this.pendingRoleId = null;
      this.roleChangeConfirm = false;
    },
    cancelRoleChange() {
      this.selectedRoleId = this.previousRoleId;
      this.pendingRoleId = null;
      this.roleChangeConfirm = false;
    },
    async save() {
      await this.$refs.form.validate();
      if (!this.valid) {
        return;
      }
      if (this.changePassword) {
        this.user.password = this.password;
      }
      else {
        this.user.password = null;
      }
      if (!this.user.firstName) {
        this.setMessage("The user must have a first name!");
        return;
      }
      if (!this.user.lastName) {
        this.setMessage("The user must have a last name!");
        return;
      }
      if (this.changePassword && !this.user.password) {
        this.setMessage("The user must have a password!");
        return;
      }
      if (!this.user.email) {
        this.setMessage("The user must have a email!");
        return;
      }
      if (!this.selectedRoleId) {
        this.setMessage("The user must have a role assigned!");
        return;
      }

      this.user.roleId = this.selectedRoleId;
      this.user.scopes = ["me", ...this.selectedScopes].join(" ");
      if (this.user && this.user.id) {
        this.update(this.user);
      } else {
        this.create(this.user);
      }
    },
  },
};
</script>

<style></style>
